/**
 * EnforcementFabricTimeline (F068)
 *
 * Pipeline view showing enforcement points across the SDLC.
 * Flow: Commit → PR Gate → Build → Test → Artifact Sign → Deploy → Promotion
 *
 * Placement: Runtime Enforcement, Policy Runs, PR Bot
 */
import React from 'react';
import { makeStyles, tokens, Badge } from '@fluentui/react-components';
import { governanceTokens } from './design-tokens';

export type EnforcementStatus = 'pass' | 'warn' | 'block' | 'not_instrumented';

export interface EnforcementPoint {
  name: string;
  status: EnforcementStatus;
  count?: number;
  lastResult?: string;
}

export interface FabricStage {
  id: string;
  label: string;
  enforcementPoints: EnforcementPoint[];
}

export interface EnforcementFabricTimelineProps {
  stages: FabricStage[];
  onStageClick?: (stageId: string) => void;
}

const statusColorMap: Record<EnforcementStatus, string> = {
  pass: governanceTokens.colorStatusSuccess,
  warn: governanceTokens.colorStatusWarning,
  block: governanceTokens.colorStatusError,
  // #475569 (slate grey) is part of the governance diagram's dark-surface accent
  // scheme, not the brand palette — no token represents it, left as-is.
  not_instrumented: '#475569',
};

const statusLabelMap: Record<EnforcementStatus, string> = {
  pass: 'Pass',
  warn: 'Warn',
  block: 'Block',
  not_instrumented: 'Not configured',
};

const statusBadgeColor: Record<EnforcementStatus, 'success' | 'warning' | 'danger' | 'informative'> = {
  pass: 'success',
  warn: 'warning',
  block: 'danger',
  not_instrumented: 'informative',
};

const useStyles = makeStyles({
  container: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0',
    overflowX: 'auto',
    padding: `${tokens.spacingVerticalM} 0`,
  },
  stage: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalXS,
    flex: '1 1 0',
    minWidth: '110px',
    cursor: 'pointer',
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground2,
    },
  },
  stageHeader: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold as any,
    color: tokens.colorNeutralForegroundStaticInverted,
    position: 'relative',
    zIndex: 1,
  },
  label: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold as any,
    color: tokens.colorNeutralForeground2,
    textAlign: 'center',
  },
  points: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    alignItems: 'center',
    width: '100%',
  },
  connector: {
    width: '24px',
    height: '2px',
    alignSelf: 'center',
    marginTop: tokens.spacingVerticalL,
    flexShrink: 0,
  },
});

export const EnforcementFabricTimeline: React.FC<EnforcementFabricTimelineProps> = ({
  stages,
  onStageClick,
}) => {
  const styles = useStyles();

  const getStageStatus = (stage: FabricStage): EnforcementStatus => {
    if (stage.enforcementPoints.some(p => p.status === 'block')) return 'block';
    if (stage.enforcementPoints.some(p => p.status === 'warn')) return 'warn';
    if (stage.enforcementPoints.every(p => p.status === 'not_instrumented')) return 'not_instrumented';
    return 'pass';
  };

  return (
    <div className={styles.container}>
      {stages.map((stage, idx) => {
        const status = getStageStatus(stage);
        return (
          <React.Fragment key={stage.id}>
            {idx > 0 && (
              <div
                className={styles.connector}
                style={{ backgroundColor: governanceTokens.colorPipelineConnector }}
              />
            )}
            <div className={styles.stage} onClick={() => onStageClick?.(stage.id)}>
              <div
                className={styles.stageHeader}
                style={{ backgroundColor: statusColorMap[status] }}
              >
                {idx + 1}
              </div>
              <span className={styles.label}>{stage.label}</span>
              <div className={styles.points}>
                {stage.enforcementPoints.map(point => (
                  <Badge
                    key={point.name}
                    appearance="tint"
                    color={statusBadgeColor[point.status]}
                    size="small"
                  >
                    {point.name}: {statusLabelMap[point.status]}
                  </Badge>
                ))}
              </div>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};
